import React, { useState } from 'react';
import {
  Card,
  CardContent,
  Box,
  Typography,
  Chip,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
} from '@mui/material';
import { Warning as WarningIcon, MoreVert as MoreVertIcon, AttachFile as AttachFileIcon, Edit as EditIcon } from '@mui/icons-material';
import DocumentViewerModal from '../DocumentViewerModal';
import { MedicalRecord } from '../../utils/api';
import { formatAnchoredDayOrInstant, formatCalendarDate } from '../../utils/dateUtils';
import { useClinicTimezone } from '../../hooks/useClinicTimezone';

interface VaccinationCardProps {
  record: MedicalRecord;
  lowStockItems?: string[];
  onMenu?: (event: React.MouseEvent<HTMLElement>, id: string) => void;
  attachment?: any;
  /** A standalone Vaccination only (it is what PUT /vaccinations/:id edits). */
  onEdit?: (vaccination: any) => void;
  canEdit?: boolean;
}

/** What the server found about a vaccination's lot (ADR-0047), in the words the record shows. */
export const STOCK_ISSUE_LABELS: Record<string, string> = {
  MISSING_LOT: 'No lot recorded',
  LOT_NOT_IN_INVENTORY: 'Lot not in inventory',
  EXPIRED_LOT: 'Expired lot used',
  LOT_SHORT: 'Lot ran short',
};

const VaccinationCard: React.FC<VaccinationCardProps> = ({ record, lowStockItems = [], onMenu, attachment, onEdit, canEdit = false }) => {
  /* THE CARD'S SUBJECT, normalised across the two shapes it is handed.
   *
   * This component was written for a `MedicalRecord` with its `vaccinations`
   * relation loaded (git d1c91994) and the callers drifted: every live caller now
   * passes a STANDALONE `Vaccination` row off `GET /vaccinations`, spread as
   * `{...v, date: v.dateAdministered}` (`HorseProfileClassic.tsx:805,831,1768`,
   * `HorseProfileModern.tsx:833,864`, `EventDetailDrawer.tsx:106`) — and
   * `Classic:798` even filters `type === 'vaccination'` medical records OUT of the
   * list, so the MedicalRecord shape never reaches the vaccination tab at all.
   *
   * A `Vaccination` has no `vaccinations` relation, so the old
   * `(record as any).vaccinations || []` was ALWAYS `[]`: the table never rendered
   * and the "No vaccinations recorded" branch won on every card. That is the
   * clinic-reported bug — a card WAS on screen, dated, but it never said which
   * vaccine was given.
   *
   * The fix reads the shape rather than the caller: prefer a populated relation
   * (a real MedicalRecord — `medical-records.service.ts:36,78,123` all
   * `include: { vaccinations: true }`), otherwise treat the row itself as the one
   * vaccination it is. `vaccineName` is the discriminator: it is `String` (NOT
   * NULL) on `Vaccination` (`schema.prisma:293`) and absent from `MedicalRecord`,
   * so it cannot false-positive on an empty-relation MedicalRecord — that case
   * still falls through to the empty state, which is correct for it.
   */
  const relation = (record as any).vaccinations;
  const vaccinations: any[] = Array.isArray(relation) && relation.length > 0
    ? relation
    : ((record as any).vaccineName ? [record] : []);
  const [viewerOpen, setViewerOpen] = useState(false);
  const clinicTz = useClinicTimezone();

  // Imported docs have notes like "[Imported Document] Title\nSource file: filename.pdf"
  const isImported = record.notes?.startsWith('[Imported Document]');
  const displayTitle = isImported
    ? record.notes!.split('\n')[0].replace('[Imported Document] ', '').trim()
    : (record.notes || 'Vaccination Record');

  return (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
          <Box>
            <Box display="flex" alignItems="center" gap={1} mb={0.5}>
              <Typography variant="h6">
                {displayTitle}
              </Typography>
              {isImported && <Chip label="Imported" size="small" variant="outlined" />}
            </Box>
            {/* PER-ROW BY SHAPE. `record.date` here is `Vaccination.dateAdministered`, not
                `MedicalRecord.date` — every live caller spreads `{...v, date:
                v.dateAdministered}` over a standalone `GET /vaccinations` row
                (`HorseProfileClassic.tsx:805,831`, `HorseProfileModern.tsx:833,864`,
                `EventDetailDrawer.tsx:106`), and Classic:798 even filters `type ===
                'vaccination'` medical records OUT of that list, so the `record: MedicalRecord`
                prop type is stale (the component now normalises both shapes — see the
                comment at the top of the component body).

                That column is MIXED per ROW (DATE-KINDS §1.2), and only ONE of its three
                writers lands on an anchor HOUR, which is what makes the shape decidable:

                  barn ops   `billing.service.ts:3284` stores `serviceDate = new Date(dto.date)`
                             and both barn screens now post `ymdToNoonUtcIso` — a picked DAY on
                             the noon anchor, `T12:00:00.000Z`. Read its date PART: re-zoning it
                             is what left these rows a day early at UTC+14.
                  web        `HorseProfileClassic.tsx:1135` / `HorseProfileModern.tsx:1252` post
                             `vaccination.dateAdministered || new Date().toISOString()`, and the
                             dialog never collects the field, so it is always the fallback — a
                             true INSTANT, at whatever millisecond the save happened.
                  iOS        `VaccinationFormView.swift:33,90` seeds `dateAdministered = Date()`
                             and edits it with a `.date`-only picker, so the device's
                             time-of-day survives into the value. Also a MOMENT.

                Same rule, same helper and same spelling set as `Payment.paymentDate` and
                `LabResult.reportDate` (§0). The upstream fix — give the dialog a date field and
                post `ymdToNoonUtcIso` — would still be an improvement, but it is no longer a
                precondition for rendering this correctly.

                ⚠ THE DISCRIMINATOR IS THE UTC TIME-OF-DAY, NOT THE MILLISECOND, and the two
                moment-writers are not immune to it. This comment claimed for one day that an
                iOS value "cannot collide with the anchor" because `.iso8601` sends no
                fractional seconds; the value is read back off a Prisma `DateTime`, which
                re-serialises through `toISOString()` and always carries `.000`. The real
                residual: a moment lands on the day arm only when its UTC time-of-day is
                exactly 00:00:00 or 12:00:00 — whole hour, minute AND second. The iOS picker
                here is `.date`-only, so the time comes from `Date()` at form-open (~1 save in
                43,200); the web fallback is `new Date().toISOString()`, which keeps its
                milliseconds, so it also needs `.000` (~1 in 43 million). Both are far below
                the rate at which the OLD render misread the barn-ops day population, which was
                every single view. Unlike `MedicalRecord.date`, no writer here offers an
                hour-and-minute picker, so there is no round-hour human choice to exclude. */}
            {/* Named for the browser matrix (`e2e/specs/chart-panels.spec.ts`), which asserts
                this exact string at UTC-11 and UTC+14. A caption-level testid, not a card-level
                one, so the assertion cannot accidentally pass on some other date in the card. */}
            <Typography
              variant="body2"
              color="textSecondary"
              data-testid={`vaccination-card-date-${(record as any).id ?? 'unknown'}`}
            >
              {formatAnchoredDayOrInstant(record.date, clinicTz, 'MMM dd, yyyy')}
            </Typography>
            {attachment?.storageUrl && (
              <>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<AttachFileIcon />}
                  onClick={() => setViewerOpen(true)}
                  sx={{ mt: 1 }}
                >
                  View Document
                </Button>
                <DocumentViewerModal
                  open={viewerOpen}
                  onClose={() => setViewerOpen(false)}
                  url={`/api${attachment.storageUrl}`}
                  title={displayTitle}
                  mimeType={attachment.mimeType}
                />
              </>
            )}
          </Box>
          {onMenu && (
            <IconButton
              size="small"
              onClick={(e) => onMenu(e, record.id)}
              title="More options"
            >
              <MoreVertIcon />
            </IconButton>
          )}
        </Box>

        {/* This table DOES render now. It previously could not: `record` is a standalone
            `Vaccination` for every live caller, which has no `vaccinations` relation, so
            the list was always `[]` and the empty state below always won. The
            normalisation at the top of the component (see that comment) is the fix; the
            empty state is now reachable only for a MedicalRecord with no vaccinations. */}
        {vaccinations.length > 0 && (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Vaccine Name</TableCell>
                  <TableCell>Date Administered</TableCell>
                  <TableCell>Administered By</TableCell>
                  <TableCell>Next Due Date</TableCell>
                  <TableCell>Lot</TableCell>
                  <TableCell>Notes</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {vaccinations.map((vacc: any, idx: number) => {
                  const isLowStock = lowStockItems.includes(vacc.vaccineName || vacc.name);
                  return (
                    <TableRow key={vacc.id || idx}>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          {vacc.vaccineName || vacc.name || '-'}
                          {isLowStock && (
                            <Chip
                              icon={<WarningIcon />}
                              label="Low Stock"
                              color="warning"
                              size="small"
                            />
                          )}
                          {(vacc.stockIssues ?? []).map((issue: string) => (
                            <Chip
                              key={issue}
                              label={STOCK_ISSUE_LABELS[issue] ?? issue}
                              color={issue === 'EXPIRED_LOT' ? 'error' : 'warning'}
                              variant="outlined"
                              size="small"
                            />
                          ))}
                        </Box>
                      </TableCell>
                      <TableCell>
                        {/* The same `Vaccination.dateAdministered` column as the card header
                            above, on the same per-row shape rule — see that comment for the
                            three writers. On the standalone shape this cell and the header
                            now render the SAME row's value, which is why they must stay on
                            the same helper. The `? :` guard stays: the helper returns '' for
                            an absent value and this cell wants a '-'. */}
                        {vacc.dateAdministered
                          ? formatAnchoredDayOrInstant(vacc.dateAdministered, clinicTz, 'MM/dd/yy')
                          : '-'}
                      </TableCell>
                      <TableCell>{vacc.administeredBy || vacc.issuedBy || '-'}</TableCell>
                      <TableCell>
                        {/* CALENDAR — `Vaccination.nextDueDate` (DATE-KINDS.md §1.1;
                            burndown §2.2). Unlike `dateAdministered` above, every writer of
                            this column stores a DAY: `vaccinations.service.ts:46,213`
                            `new Date(dto.nextDueDate)` over the noon-UTC value
                            `VaccinationDialog.tsx:138` now posts via
                            `datePickerToCalendarValue`, and `barnOperationsItems.ts:101` at
                            UTC midnight. Re-zoning either anchor moves the day (midnight
                            back at UTC-11, noon forward at UTC+14); the date PART moves for
                            neither. It also feeds the ADR-0019 recall reminder, so the day a
                            client is chased on is this string.
                            (`nextBooster` is an INPUT-only DTO field — `medical-records.
                            service.ts:302` folds it into `nextDueDate` — so this fallback
                            never resolves on a response. Kept, not widened.) */}
                        {vacc.nextDueDate || vacc.nextBooster ? (
                          <Typography variant="caption">
                            {formatCalendarDate(vacc.nextDueDate || vacc.nextBooster, 'MM/dd/yy')}
                          </Typography>
                        ) : (
                          '-'
                        )}
                      </TableCell>
                      <TableCell>
                        {/* CALENDAR `Vaccination.lotExpiresOn` (@db.Date): the date part, never re-zoned. */}
                        {vacc.lotNumber ? (
                          <Typography variant="caption" display="block">
                            {vacc.lotNumber}
                            {vacc.lotExpiresOn ? ` · exp ${formatCalendarDate(vacc.lotExpiresOn, 'MM/dd/yy')}` : ''}
                            {vacc.manufacturer ? ` · ${vacc.manufacturer}` : ''}
                          </Typography>
                        ) : (
                          '-'
                        )}
                        {vacc.doseMl ? (
                          <Typography variant="caption" color="text.secondary" display="block">
                            {vacc.doseMl} mL
                          </Typography>
                        ) : null}
                      </TableCell>
                      <TableCell>{vacc.notes || vacc.description || '-'}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {vaccinations.length === 0 && (
          <Typography variant="body2" color="textSecondary">
            No vaccinations recorded
          </Typography>
        )}
        {canEdit && onEdit && (record as any).vaccineName && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
            <Button size="small" variant="outlined" startIcon={<EditIcon />} onClick={() => onEdit(record)}>
              Edit Vaccination
            </Button>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default VaccinationCard;
